import { createSlice } from '@reduxjs/toolkit';
import {getValuesByPaths} from "../utils";
import {
    deleteUserAppData, saveUserAppData, updateUser, fetchUserFields, createProspect, updateProspectPortfolios,
    createPage, updatePage, deletePages, setUserFields, createTouchpoint, updateTouchpoint, deleteTouchpoint,
    fetchTouchpoint, listPageViews
} from "../../api/userApi";
import { listFiles, uploadFiles, renameFile, renameFolder, deleteResources } from "../../api/filesApi";
import { fetchUpcomingEvents } from "../../api/tickersApi";


const handleCreateFulfilled = (state, action, dataKey) => {
  state.status = 'succeeded';
  if (!state.data[dataKey]) {
    state.data[dataKey] = [action.payload];
  } else {
    state.data[dataKey].push(action.payload);
  }
};



const userSlice = createSlice({
  name: 'user',
  initialState: {
    data: null, // Includes authentication details and app_data
    files: null,
    filesLoaded: false,
    upcomingEvents: null,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,

  },
  reducers: {
      setUserData: (state, action) => {
          console.log('Setting user data:', action.payload);
          state.data = {...action.payload};
      },
  },
  extraReducers: (builder) => {
      builder
          .addCase(createProspect.fulfilled, (state, action) => {
              handleCreateFulfilled(state, action, 'prospects');
          })
          .addCase(createPage.fulfilled, (state, action) => {
              handleCreateFulfilled(state, action, 'page_details');
          })
          .addCase(createTouchpoint.fulfilled, (state, action) => {
              handleCreateFulfilled(state, action, 'touchpoint_details');
          })
          .addCase(updateUser.fulfilled, (state, action) => {
              const {responseData, updateData} = action.payload;
              state.status = 'succeeded';
          })
          .addCase(setUserFields.fulfilled, (state, action) => {
              state.status = 'succeeded';
              console.log(action.payload);
              state.data = {
                  ...state.data,
                  ...action.payload
              }
          })

        .addCase(saveUserAppData.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const { id, data } = action.payload;
            console.log(id, data);

            if (!id) {
                // This is a new app data entry and data is nested under the 'data' key
                state.data.app_data = [...state.data.app_data, data?.data];
            } else {
                // This is an update to an existing app data entry
                state.data.app_data = state.data.app_data.map(item =>
                    item.id === id ? { ...item, data: data } : item
                );
            }
        })
        .addCase(deleteUserAppData.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data.app_data = state.data.app_data.filter(item => !action.payload?.ids?.includes?.(item.id));
        })
        .addCase(fetchUserFields.fulfilled, (state, action) => {
          if (action.payload) {
            state.data = {
              ...state.data,
              ...action.payload,
            };
          }
    })
        .addCase(updateTouchpoint.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const index = state.data.touchpoint_details.findIndex(
              (page) => page.id === action.payload.touchpointId
            );
            if (index !== -1) {
              state.data.touchpoint_details[index] = {id: action.payload.touchpointId, ...action.payload.touchpointData}
            }
        })
        .addCase(fetchTouchpoint.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const touchpointId = action.meta.arg;  // Assuming the touchpoint ID is passed as the argument
            const index = state.data.touchpoint_details.findIndex(tp => tp.id === touchpointId);
            if (index !== -1) {
                // If the touchpoint is found, update its content
                state.data.touchpoint_details[index].content = action.payload;
            } else {
                // Otherwise, add new touchpoint detail with content
                state.data.touchpoint_details.push({
                    id: touchpointId,
                    content: action.payload
                });
            }
          })
        .addCase(updatePage.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const index = state.data.page_details.findIndex(
              (page) => page.page_id === action.payload.pageId
            );
            if (index !== -1) {
              state.data.page_details[index] = {page_id: action.payload.pageId, ...action.payload.pageData}
            }
        })
        .addCase(deletePages.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data.page_details = state.data.page_details.filter(page => !action.payload.includes(page.page_id));
        })
        .addCase(deleteTouchpoint.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data.touchpoint_details = state.data.touchpoint_details.filter(touchpoint => touchpoint.id !== action.payload);
        })
        .addCase(createProspect.rejected, (state, action) => {
          state.status = 'failed';
          state.error = action.payload;
        })
        .addCase(updateProspectPortfolios.fulfilled, (state, action) => {
            // Assuming you have a way to find and update the specific prospect within your state
            const index = state.data.prospects.findIndex(prospect => prospect.userId === action.payload.prospectUserId);
            if (index !== -1) {
                state.data.prospects[index].portfolios = action.payload.portfolios;
            }
        })
        .addCase(listFiles.fulfilled, (state, action) => {
            state.status = 'succeeded';
            console.log(action.payload);
            state.files = [...(state.files || []), ...action.payload];
            state.filesLoaded = true; // Set filesLoaded to true

        })
        .addCase(listPageViews.fulfilled, (state, action) => {
          state.status = 'succeeded';

          const { pageId, pageViews } = action.payload;

          // Find the page with the matching pageId
          const pageIndex = state.data.page_details.findIndex(page => page.page_id === pageId);

          if (pageIndex !== -1) {
            // If the page is found, append the pageViews
            state.data.page_details[pageIndex].pageViews = pageViews;
          } else {
            // If the page is not found, create a new entry
            state.data.page_details.push({
              page_id: pageId,
              pageViews,
            });
          }
        })
        .addCase(uploadFiles.pending, (state) => {
            state.status = 'loading';
        })
        .addCase(uploadFiles.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.files = [...(state.files || []), ...action.payload];
        })
        .addCase(uploadFiles.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
        .addCase(fetchUpcomingEvents.fulfilled, (state, action)=>{
            state.status = 'succeeded';
            state.upcomingEvents = action.payload;
        })
        .addCase(renameFile.fulfilled, (state, action) => {
          const fileIndex = state.files.findIndex(file => file.path === action.payload.oldPath);

          // Check if the file was found
          if (fileIndex !== -1) {
            // Update the path of the file to the new path
            state.files[fileIndex] = {
              ...state.files[fileIndex],
              path: `${action.payload.newPath}.${state.files[fileIndex].ext}`
            };
          }
      })
      .addCase(renameFile.rejected, (state, action) => {
        console.log('Failed to rename file:', action.error.message);
      })
      .addCase(renameFolder.fulfilled, (state, action) => {
        console.log('Folder renamed successfully:', action.payload);
      })
      .addCase(renameFolder.rejected, (state, action) => {
        console.log('Failed to rename folder:', action.error.message);
      })
  },
});


export const selectAppDataByType = (state, appType) => {
  return state.user.data?.app_data.filter(item => item.app_type === appType) || [];
};

export const selectAppDataById = (state, id) => {
    return state.user?.data?.app_data.find(item => item.id === id);
}


export const selectUserFields = (state, paths) => {
  return getValuesByPaths(state.user.data, paths);
};


export const { setUserData } = userSlice.actions;
export default userSlice.reducer;
